/**
 * Push notification helpers — Phase A.
 *
 * Anonymous-first pattern: a user can grant push permission and subscribe
 * before signing up. The backend records the sub with `user_id=null`. When
 * they later sign up or log in from the same browser, the next
 * subscribe-on-load call links the sub to their user_id.
 */
import api from "./api";
import { getFollowerId } from "./follows";
import { Capacitor } from "@capacitor/core";

/** Returns true if this browser has the APIs we need to even attempt push. */
export function isPushSupported() {
  return (
    typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window
  );
}

/** True if running as an installed PWA (necessary for push on iOS Safari)
 *  — or inside the native Capacitor app, which is "installed" by definition
 *  (prevents the Safari add-to-home-screen coachmark from ever showing
 *  inside the real app). */
export function isStandalone() {
  if (Capacitor.isNativePlatform()) return true;
  return (
    typeof window !== "undefined"
    && (window.matchMedia?.("(display-mode: standalone)").matches
      || window.navigator?.standalone === true)
  );
}

/** Heuristic: iOS Safari (incl. Chrome-on-iOS, which is also WebKit). */
export function isIosLike() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export function permissionState() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

async function getRegistration() {
  // sw.js is registered globally by serviceWorkerRegistration.js — we just
  // wait for whichever is ready.
  return navigator.serviceWorker.ready;
}

function pushSubToBody(sub) {
  const json = sub.toJSON();
  return {
    endpoint: json.endpoint,
    keys: json.keys,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    follower_id: getFollowerId(),
  };
}

/**
 * Get the active subscription for this browser, requesting permission
 * and creating one if needed. Returns the raw `PushSubscription` object
 * (not the JSON body). Used by `enablePush` and `enablePushForTruck`.
 */
async function ensureSubscription() {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };
  if (isIosLike() && !isStandalone()) return { ok: false, reason: "ios_needs_pwa" };
  let perm = Notification.permission;
  if (perm === "default") perm = await Notification.requestPermission();
  if (perm !== "granted") return { ok: false, reason: perm };

  const { data: keyResp } = await api.get("/push/vapid-public-key");
  if (!keyResp?.public_key) return { ok: false, reason: "no_vapid_key" };

  const reg = await getRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(keyResp.public_key),
    });
  }
  return { ok: true, sub };
}

/**
 * Full enable flow: request permission, subscribe, send to backend.
 * Returns `{ok: true}` on success, otherwise `{ok: false, reason}` —
 * caller decides how to surface the failure.
 */
export async function enablePush() {
  const r = await ensureSubscription();
  if (!r.ok) return r;
  await api.post("/push/subscribe", pushSubToBody(r.sub));
  return { ok: true, endpoint: r.sub.endpoint };
}

/**
 * Per-truck "Notify me when live" — subscribe to push (if not already)
 * AND follow the truck in a single round-trip.
 * Returns `{ok: true}` on success, otherwise `{ok: false, reason}`.
 */
export async function enablePushForTruck(truckId) {
  // Native app: the APNs/FCM subscription is registered at launch by
  // useNativePush — here we only need OS permission + the follow row.
  if (Capacitor.isNativePlatform()) {
    try {
      const { PushNotifications } = await import("@capacitor/push-notifications");
      let perm = await PushNotifications.checkPermissions();
      if (perm.receive !== "granted") perm = await PushNotifications.requestPermissions();
      if (perm.receive !== "granted") return { ok: false, reason: "denied" };
      PushNotifications.register().catch(() => {});
    } catch {
      return { ok: false, reason: "native_unavailable" };
    }
    await api.post("/push/subscribe-truck", {
      endpoint: `native://${getFollowerId()}`,
      keys: { p256dh: "native", auth: "native" },
      follower_id: getFollowerId(),
      truck_id: truckId,
      transport: "native",
    });
    return { ok: true, endpoint: null };
  }
  const r = await ensureSubscription();
  if (!r.ok) return r;
  const body = { ...pushSubToBody(r.sub), truck_id: truckId };
  await api.post("/push/subscribe-truck", body);
  return { ok: true, endpoint: r.sub.endpoint };
}

/** Stop receiving truck_live pings for one truck (keeps push enabled). */
export async function disablePushForTruck(truckId) {
  const reg = isPushSupported() ? await getRegistration() : null;
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  // Still send the unsubscribe even if the sub vanished — the follow row
  // is keyed on follower_id, so the backend doesn't actually need the
  // endpoint to delete it.
  const body = {
    endpoint: sub?.endpoint || "https://noop.invalid/x",
    keys: sub ? sub.toJSON().keys : { p256dh: "noop", auth: "noop" },
    follower_id: getFollowerId(),
    truck_id: truckId,
  };
  try { await api.post("/push/unsubscribe-truck", body); } catch { /* ignore */ }
}

/** Look up which trucks this follower will receive live-pings for. */
export async function getNotifyStatus() {
  try {
    const fid = getFollowerId();
    const { data } = await api.get(`/push/notify-status?follower_id=${encodeURIComponent(fid)}`);
    return {
      truckIds: Array.isArray(data?.truck_ids) ? data.truck_ids : [],
      hasActiveSubscription: !!data?.has_active_subscription,
    };
  } catch {
    return { truckIds: [], hasActiveSubscription: false };
  }
}

/** Disable push for this browser. Removes the sub from the SW + backend. */
export async function disablePush() {
  if (!isPushSupported()) return;
  const reg = await getRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch { /* ignore */ }
  try { await api.post("/push/unsubscribe", { endpoint }); } catch { /* ignore */ }
}

/**
 * Re-link the existing browser subscription to the (now logged-in) user.
 * Cheap idempotent re-POST whenever auth changes — backend upserts on
 * endpoint and stamps the new user_id.
 */
export async function relinkSubscriptionToCurrentUser() {
  if (!isPushSupported()) return;
  if (Notification.permission !== "granted") return;
  try {
    const reg = await getRegistration();
    const sub = await reg.pushManager.getSubscription();
    if (sub) await api.post("/push/subscribe", pushSubToBody(sub));
  } catch {
    // never block UI on push relink
  }
}
